// LinkedIn Integration Implementation
// Provides integration with LinkedIn Marketing API for company pages and content publishing

import { BaseSocialIntegration, SocialPlatform, SocialPost, SocialEngagement, SocialAudience } from '../base/BaseSocialIntegration';
import { IntegrationConfig, ConnectionResult, TestResult, ValidationResult } from '../../../types/integration';

interface LinkedInCredentials {
  accessToken: string;
  clientId: string;
  clientSecret: string;
  organizationId?: string;
  personUrn?: string;
}

interface LinkedInOrganization {
  id: string;
  name: string;
  vanityName: string;
  logoV2?: {
    original: string;
  };
  organizationType: string;
  followerCount: number;
}

interface _LinkedInPostResponse {
  id: string;
  created: {
    time: number;
  };
  lastModified: {
    time: number;
  };
  author: string;
  lifecycleState: 'PUBLISHED' | 'DRAFT';
  specificContent: {
    'com.linkedin.ugc.ShareContent': {
      shareCommentary: {
        text: string;
      };
      shareMediaCategory: 'NONE' | 'IMAGE' | 'VIDEO' | 'ARTICLE';
      media?: Array<{
        status: 'READY' | 'PROCESSING' | 'FAILED';
        description: {
          text: string;
        };
        media: string;
        title: {
          text: string;
        };
      }>;
    };
  };
}

interface _LinkedInAnalytics {
  elements: Array<{
    totalShareStatistics: {
      shareCount: number;
      likeCount: number;
      commentCount: number;
      clickCount: number;
      impressionCount: number;
      uniqueImpressionsCount: number;
    };
  }>;
}

export class LinkedInIntegration extends BaseSocialIntegration {
  private credentials: LinkedInCredentials;
  private baseUrl = 'https://api.linkedin.com/v2';
  private connectedOrganizations: LinkedInOrganization[] = [];
  private isConnected = false;
  private lastConnectedAt?: Date;
  private lastSyncAt?: Date;

  constructor(config: IntegrationConfig) {
    super(config);
    // Extract credentials from encrypted format - in real implementation would decrypt
    this.credentials = (config.credentials as unknown as LinkedInCredentials);
  }

  async connect(): Promise<ConnectionResult> {
    try {
      // Verify access token and get user profile
      const profileResponse = await this.makeRequest('/people/~', {
        projection: '(id,firstName,lastName,profilePicture)'
      });

      // Get organizations the user can manage
      const organizationsResponse = await this.makeRequest('/organizationAcls', {
        q: 'roleAssignee',
        projection: '(elements*(organization~(id,name,vanityName,logoV2,organizationType),roleAssignee,role))'
      });

      // Filter organizations where user has admin or content admin role
      const managedOrgs = organizationsResponse.elements
        .filter((acl: any) => 
          acl.role === 'ADMINISTRATOR' || 
          acl.role === 'CONTENT_ADMIN' ||
          acl.role === 'ORGANIC_POSTER'
        )
        .map((acl: any) => acl['organization~']);

      // Get follower counts for organizations
      for (const org of managedOrgs) {
        try {
          const followersResponse = await this.makeRequest(`/networkSizes/urn:li:organization:${org.id}`, {
            edgeType: 'CompanyFollowedByMember'
          });
          org.followerCount = followersResponse.firstDegreeSize || 0;
        } catch (error) {
          console.warn(`Failed to get follower count for organization ${org.id}:`, error);
          org.followerCount = 0;
        }
      }

      this.connectedOrganizations = managedOrgs;
      
      // Update connection status
      this.isConnected = true;
      this.lastConnectedAt = new Date();

      console.log('LinkedIn connected:', {
        profile: `${profileResponse.firstName?.localized?.en_US} ${profileResponse.lastName?.localized?.en_US}`,
        organizationsCount: this.connectedOrganizations.length
      });

      return {
        success: true,
        message: `Connected to LinkedIn with ${this.connectedOrganizations.length} managed organizations`,
        connectionId: `linkedin_${Date.now()}`,
        capabilities: ['post_publishing', 'analytics', 'organization_management'],
        metadata: {
          organizationsCount: this.connectedOrganizations.length,
          userProfile: profileResponse.id
        }
      };

    } catch (error) {
      console.error('LinkedIn connection failed:', error instanceof Error ? error.message : 'Unknown error');
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  async disconnect(): Promise<void> {
    try {
      this.isConnected = false;
      this.connectedOrganizations = [];
      
      console.log('LinkedIn disconnected');
    } catch (error) {
      console.error('LinkedIn disconnection failed:', error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  }

  async testConnection(): Promise<TestResult> {
    const startTime = Date.now();
    try {
      const response = await this.makeRequest('/people/~', {
        projection: '(id,firstName,lastName)'
      });
      
      if (response.id) {
        const name = `${response.firstName?.localized?.en_US || ''} ${response.lastName?.localized?.en_US || ''}`.trim();
        return {
          success: true,
          message: `Connected as ${name || response.id}`,
          duration: Date.now() - startTime,
          details: { userId: response.id, userName: name }
        };
      }
      
      return {
        success: false,
        message: 'Invalid response from LinkedIn API',
        duration: Date.now() - startTime
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Connection test failed',
        duration: Date.now() - startTime
      };
    }
  }

  async validateConfig(): Promise<ValidationResult> {
    const errors: Array<{ field: string; message: string; code: string }> = [];
    const warnings: Array<{ field: string; message: string; suggestion?: string }> = [];
    
    if (!this.credentials.accessToken) {
      errors.push({ field: 'accessToken', message: 'Access token is required', code: 'REQUIRED' });
    }
    
    if (!this.credentials.clientId) {
      errors.push({ field: 'clientId', message: 'Client ID is required', code: 'REQUIRED' });
    }
    
    if (!this.credentials.clientSecret) {
      errors.push({ field: 'clientSecret', message: 'Client secret is required', code: 'REQUIRED' });
    }
    
    // Add warnings for optional fields
    if (!this.credentials.organizationId) {
      warnings.push({ 
        field: 'organizationId', 
        message: 'Organization ID not specified - will auto-detect from access token',
        suggestion: 'Specify an organization ID for better performance'
      });
    }
    
    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  async getConnectedPlatforms(): Promise<SocialPlatform[]> {
    const platforms: SocialPlatform[] = [];

    for (const org of this.connectedOrganizations) {
      platforms.push({
        id: org.id,
        name: 'linkedin',
        displayName: `LinkedIn - ${org.name}`,
        connected: true,
        accountId: org.id,
        accountName: org.name,
        permissions: ['w_member_social', 'w_organization_social', 'r_organization_social'],
        connectedAt: this.lastConnectedAt || new Date(),
        lastSyncAt: this.lastSyncAt
      });
    }

    return platforms;
  }

  async connectPlatform(platform: SocialPlatform['name'], credentials: any): Promise<void> {
    if (platform === 'linkedin') {
      this.credentials = { ...this.credentials, ...credentials };
      await this.connect();
    } else {
      throw new Error(`Platform ${platform} not supported by LinkedIn integration`);
    }
  }

  async disconnectPlatform(platform: SocialPlatform['name']): Promise<void> {
    if (platform === 'linkedin') {
      await this.disconnect();
    } else {
      throw new Error(`Platform ${platform} not supported by LinkedIn integration`);
    }
  }

  async createPost(postData: Omit<SocialPost, 'id' | 'engagement'>): Promise<SocialPost> {
    try {
      if (postData.platform !== 'linkedin') {
        throw new Error(`Platform ${postData.platform} not supported`);
      }

      const organizationUrn = this.getOrganizationUrn(postData.targetAudience?.id);
      
      const shareContent: any = {
        author: organizationUrn,
        lifecycleState: 'PUBLISHED',
        specificContent: {
          'com.linkedin.ugc.ShareContent': {
            shareCommentary: {
              text: postData.content
            },
            shareMediaCategory: postData.media && postData.media.length > 0 ? 'IMAGE' : 'NONE'
          }
        },
        visibility: {
          'com.linkedin.ugc.MemberNetworkVisibility': 'PUBLIC'
        }
      };

      // Handle media attachments
      if (postData.media && postData.media.length > 0) {
        const media = postData.media[0]; // LinkedIn supports one media per post
        
        if (media.type === 'image') {
          // Upload image first
          const uploadedAsset = await this.uploadImage(media.url, organizationUrn);
          
          shareContent.specificContent['com.linkedin.ugc.ShareContent'].media = [{
            status: 'READY',
            description: {
              text: media.altText || ''
            },
            media: uploadedAsset,
            title: {
              text: ''
            }
          }];
        }
      }

      const response = await this.makeRequest('/ugcPosts', shareContent, 'POST');

      const post: SocialPost = {
        id: response.id,
        platform: 'linkedin',
        content: postData.content,
        media: postData.media,
        status: 'published',
        publishedTime: new Date(),
        engagement: {
          likes: 0,
          comments: 0,
          shares: 0,
          clicks: 0,
          impressions: 0,
          reach: 0,
          engagementRate: 0,
          lastUpdated: new Date()
        },
        hashtags: postData.hashtags || [],
        mentions: postData.mentions || [],
        campaignId: postData.campaignId
      };

      console.log('LinkedIn post created:', { post });
      
      return post;
    } catch (error) {
      console.error('LinkedIn post creation failed:', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async updatePost(postId: string, _updates: Partial<SocialPost>): Promise<SocialPost> {
    try {
      // LinkedIn doesn't allow editing published posts
      throw new Error('LinkedIn does not support editing published posts');
    } catch (error) {
      console.error('LinkedIn post update failed:', {
        postId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async deletePost(postId: string): Promise<void> {
    try {
      await this.makeRequest(`/ugcPosts/${postId}`, {}, 'DELETE');
      
      console.log('LinkedIn post deleted:', { postId });
    } catch (error) {
      console.error('LinkedIn post deletion failed:', {
        postId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async getPost(postId: string): Promise<SocialPost | null> {
    try {
      const response = await this.makeRequest(`/ugcPosts/${postId}`);

      if (!response.id) return null;

      const engagement = await this.getEngagementData(postId);

      return {
        id: response.id,
        platform: 'linkedin',
        content: response.specificContent?.['com.linkedin.ugc.ShareContent']?.shareCommentary?.text || '',
        status: response.lifecycleState === 'PUBLISHED' ? 'published' : 'draft',
        publishedTime: new Date(response.created.time),
        engagement,
        hashtags: this.extractHashtags(response.specificContent?.['com.linkedin.ugc.ShareContent']?.shareCommentary?.text || ''),
        mentions: this.extractMentions(response.specificContent?.['com.linkedin.ugc.ShareContent']?.shareCommentary?.text || '')
      };
    } catch (error) {
      console.error('Failed to get LinkedIn post:', error);
      return null;
    }
  }

  async schedulePost(postData: Omit<SocialPost, 'id' | 'engagement'>, scheduledTime: Date): Promise<SocialPost> {
    try {
      // LinkedIn doesn't support native post scheduling through API
      // This would typically be handled by a scheduling service
      throw new Error('LinkedIn post scheduling requires third-party scheduling service');
    } catch (error) {
      console.error('LinkedIn post scheduling failed:', {
        scheduledTime,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async publishPost(postId: string): Promise<SocialPost> {
    try {
      // For draft posts, this would publish them
      const updateData = {
        lifecycleState: 'PUBLISHED'
      };

      await this.makeRequest(`/ugcPosts/${postId}`, updateData, 'POST');

      const post = await this.getPost(postId);
      if (!post) {
        throw new Error('Failed to retrieve published post');
      }

      post.status = 'published';
      post.publishedTime = new Date();

      console.log('LinkedIn post published:', { post });
      
      return post;
    } catch (error) {
      console.error('LinkedIn post publishing failed:', {
        postId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async getEngagementData(postId: string): Promise<SocialEngagement> {
    try {
      const response = await this.makeRequest(`/socialActions/${postId}/statistics`, {
        projection: '(totalShareStatistics)'
      });

      const stats = response.totalShareStatistics || {};

      return {
        likes: stats.likeCount || 0,
        comments: stats.commentCount || 0,
        shares: stats.shareCount || 0,
        clicks: stats.clickCount || 0,
        impressions: stats.impressionCount || 0,
        reach: stats.uniqueImpressionsCount || 0,
        engagementRate: this.calculateEngagementRate(stats),
        lastUpdated: new Date()
      };
    } catch (error) {
      console.error('Failed to get LinkedIn engagement data:', error);
      return {
        likes: 0,
        comments: 0,
        shares: 0,
        clicks: 0,
        impressions: 0,
        reach: 0,
        engagementRate: 0,
        lastUpdated: new Date()
      };
    }
  }

  async createAudience(audienceData: Omit<SocialAudience, 'id'>): Promise<SocialAudience> {
    try {
      // LinkedIn doesn't have a direct audience creation API like Facebook
      // This would typically involve creating a campaign audience or using matched audiences
      throw new Error('LinkedIn audience creation requires LinkedIn Campaign Manager API');
    } catch (error) {
      console.error('LinkedIn audience creation failed:', {
        audienceName: audienceData.name,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async syncAudience(audienceId: string, _emails: string[]): Promise<void> {
    try {
      // LinkedIn audience sync would be handled through Campaign Manager API
      throw new Error('LinkedIn audience sync requires LinkedIn Campaign Manager API');
    } catch (error) {
      console.error('LinkedIn audience sync failed:', {
        audienceId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async createLookalikeAudience(sourceAudienceId: string, similarity: number): Promise<SocialAudience> {
    try {
      // LinkedIn lookalike audiences are created through Campaign Manager
      throw new Error('LinkedIn lookalike audience creation requires LinkedIn Campaign Manager API');
    } catch (error) {
      console.error('LinkedIn lookalike audience creation failed:', {
        sourceAudienceId,
        similarity,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  // Private helper methods

  private async uploadImage(imageUrl: string, organizationUrn: string): Promise<string> {
    try {
      // Step 1: Register upload
      const registerResponse = await this.makeRequest('/assets', {
        registerUploadRequest: {
          recipes: ['urn:li:digitalmediaRecipe:feedshare-image'],
          owner: organizationUrn,
          serviceRelationships: [{
            relationshipType: 'OWNER',
            identifier: 'urn:li:userGeneratedContent'
          }]
        }
      }, 'POST');

      const uploadUrl = registerResponse.value.uploadMechanism['com.linkedin.digitalmedia.uploading.MediaUploadHttpRequest'].uploadUrl;
      const asset = registerResponse.value.asset;

      // Step 2: Upload image
      const imageResponse = await fetch(imageUrl);
      const imageBlob = await imageResponse.blob();

      await fetch(uploadUrl, {
        method: 'POST',
        body: imageBlob,
        headers: {
          'Authorization': `Bearer ${this.credentials.accessToken}`
        }
      });

      return asset;
    } catch (error) {
      console.error('LinkedIn image upload failed:', error);
      throw error;
    }
  }

  private async makeRequest(endpoint: string, data: any = {}, method: 'GET' | 'POST' | 'DELETE' = 'GET'): Promise<any> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${this.credentials.accessToken}`,
      'Content-Type': 'application/json',
      'X-Restli-Protocol-Version': '2.0.0'
    };

    let response: Response;
    
    if (method === 'GET') {
      const params = new URLSearchParams();
      Object.keys(data).forEach(key => {
        params.append(key, data[key]);
      });
      
      const urlWithParams = params.toString() ? `${url}?${params.toString()}` : url;
      response = await fetch(urlWithParams, { method, headers });
    } else if (method === 'DELETE') {
      response = await fetch(url, { method, headers });
    } else {
      response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(data)
      });
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`LinkedIn API error: ${response.status} - ${errorData.message || response.statusText}`);
    }

    return await response.json();
  }

  private getOrganizationUrn(organizationId?: string): string {
    if (organizationId) {
      return `urn:li:organization:${organizationId}`;
    }
    
    if (this.connectedOrganizations.length === 0) {
      throw new Error('No organizations connected');
    }
    
    // Use first organization as default
    return `urn:li:organization:${this.connectedOrganizations[0].id}`;
  }

  private extractHashtags(content: string): string[] {
    const hashtagRegex = /#[\w]+/g;
    return content.match(hashtagRegex) || [];
  }

  private extractMentions(content: string): string[] {
    const mentionRegex = /@[\w]+/g;
    return content.match(mentionRegex) || [];
  }

  private calculateEngagementRate(stats: any): number {
    const totalEngagements = (stats.likeCount || 0) + (stats.commentCount || 0) + (stats.shareCount || 0);
    const impressions = stats.impressionCount || 0;
    
    if (impressions === 0) return 0;
    
    return (totalEngagements / impressions) * 100;
  }

  /**
   * Get the last sync time for this integration
   */
  getLastSyncTime(): Date | undefined {
    return this.lastSyncAt;
  }

  /**
   * Update the last sync time
   */
  protected updateLastSyncTime(): void {
    this.lastSyncAt = new Date();
  }
}